<template>
  <div
    class="play-spin"
    :class="[`play-spin--${size}`, { 'is-disabled': disabled, 'is-error': !!error }]"
    role="group"
  >
    <button
      type="button"
      class="play-spin__btn"
      :disabled="disabled || num <= Number(min)"
      aria-label="Decrease"
      @click="bump(-1)"
    >
      <feather-icon
        icon="MinusIcon"
        size="16"
      />
    </button>

    <input
      class="play-spin__value"
      type="text"
      inputmode="numeric"
      :value="num"
      :disabled="disabled"
      @input="onType"
      @blur="commit"
    >

    <button
      type="button"
      class="play-spin__btn"
      :disabled="disabled || num >= Number(max)"
      aria-label="Increase"
      @click="bump(1)"
    >
      <feather-icon
        icon="PlusIcon"
        size="16"
      />
    </button>
  </div>
</template>

<script>
/**
 * PlaySpinbutton — numeric stepper (−/value/+). Replaces bootstrap-vue's
 * <b-form-spinbutton>. v-model driven; clamps to [min, max] on step, type and
 * blur. Emits `input` continuously and `change` on a committed value.
 */
export default {
  name: 'PlaySpinbutton',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [Number, String],
      default: 0,
    },
    min: {
      type: [Number, String],
      default: 0,
    },
    max: {
      type: [Number, String],
      default: 100,
    },
    step: {
      type: [Number, String],
      default: 1,
    },
    /** no-op — bootstrap-vue compat (the group is always inline here) */
    inline: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
  },
  computed: {
    num() {
      const n = Number(this.value)
      return Number.isNaN(n) ? Number(this.min) : n
    },
  },
  methods: {
    clamp(n) {
      return Math.min(Number(this.max), Math.max(Number(this.min), n))
    },
    bump(dir) {
      if (this.disabled) return
      const next = this.clamp(this.num + dir * Number(this.step))
      this.emit(next)
      this.$emit('change', next)
    },
    onType(e) {
      const raw = e.target.value.replace(/[^\d.-]/g, '')
      if (raw === '' || raw === '-') return
      this.emit(this.clamp(Number(raw)))
    },
    commit() {
      const next = this.clamp(this.num)
      this.emit(next)
      this.$emit('change', next)
    },
    emit(n) {
      this.$emit('input', n)
    },
  },
}
</script>

<style lang="scss" scoped>
.play-spin {
  display: inline-flex;
  align-items: stretch;
  background: var(--surface-sunk);
  border: var(--border-w) solid transparent;
  border-radius: var(--radius);
  overflow: hidden;
  transition:
    background var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out);

  /* subtle focus cue only — no violet ring lingering after a +/- click
     (Safari flags the mouse click as focus and keeps :focus-within) */
  &:focus-within {
    background: var(--surface-card);
    border-color: var(--line);
  }

  &__btn {
    display: grid;
    place-items: center;
    flex: none;
    width: 40px;
    border: none;
    background: transparent;
    color: var(--c-secondary-strong);
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);

    &:hover:not(:disabled) { background: var(--c-secondary-soft); }
    &:disabled { color: var(--text-muted); cursor: not-allowed; opacity: .55; }
    /* no focus ring lingering on the +/- buttons after a click */
    &:focus,
    &:focus-visible { outline: none; }
  }

  &__value {
    width: 64px;
    min-width: 0;
    border: none;
    background: transparent;
    text-align: center;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 15px;
    color: var(--text-strong);
    outline: none;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  }

  /* sizes */
  &--sm {
    border-radius: var(--radius-sm);
    .play-spin__btn { width: 34px; }
    .play-spin__value { width: 52px; font-size: 14px; padding: 7px 0; }
  }
  &--md .play-spin__value { padding: 10px 0; }
  &--lg {
    border-radius: var(--radius-lg);
    .play-spin__btn { width: 46px; }
    .play-spin__value { width: 74px; font-size: 16px; padding: 13px 0; }
  }

  &.is-error {
    background: var(--c-danger-soft);
    &:focus-within { border-color: var(--c-danger); box-shadow: 0 0 0 3px var(--c-danger-soft); }
  }
  &.is-disabled { opacity: .6; pointer-events: none; }
}
</style>
